import React, { useState, useEffect, Fragment } from 'react';
import { connect } from 'react-redux';
import cx from 'classnames';
import { Trans, withI18n } from '@lingui/react';
import { Manager, Reference, Popper } from 'react-popper';
import { FaExpandArrowsAlt, FaTimes, FaPercent } from 'react-icons/fa';
import sortBy from 'lodash/sortBy';
import includes from 'lodash/includes';
import find from 'lodash/find';
import last from 'lodash/last';
import xor from 'lodash/xor';
import isArray from 'lodash/isArray';

import ArticleRecap from './ArticleRecap';
import ArticleDetailsCategoryTab from './ArticleDetailsCategoryTab';
import { getAssetURL, getProductImageUrl } from '../api';
import { selectors } from '../reducers/appReducer';
import { ARTICLE_DETAIL_SECTIONS } from '../constants';
import {
    getScontoAumento,
    getValidCategories,
    getImgContainerStyle,
    getMarcaArticolo, getCodiceArticolo, getDescrizioneArticolo
} from './ArticleComponents/ArticleDetails/ArticleDetailsBodyUtils';
import ModalScontoAumento from './ArticleComponents/ArticleDetails/ModalScontoAumento';
import ArticlePrintWindow from './ArticleComponents/ArticleDetails/ArticlePrintWindow';
import { useAssetPreview } from '../hooks/useAssetPreview';
import noPhotoImg from '../images/no-photo.png';
import { Img } from 'react-image';

const categories = Object.values(ARTICLE_DETAIL_SECTIONS);
const orderedCategoriesKeys = sortBy(categories, 'order').map((c) => c.key);

/**
 *
 * @param article è l' id dell' articolo
 * @param details è l' object con dentro tutti i valori dell' articolo (inclusi gli assets)
 * @param isPreview flag per determinare se è una anteprima (?)
 * @param productProperties sono le label più la tipologia di field di ogni valore dell' articolo
 * @param etimClassDetails è l' id e la label della classificazione ETIM a cui appartiene o no l' articolo
 * @param scrollContainer è l' id del div della modale dell' articolo
 * @param i18n
 * @param language è la lingua corrente
 * @param initialCategories
 * @param onSelectTab
 * @param state
 * @returns {JSX.Element}
 * @constructor
 */
const ArticleDetailsBody = ({
        article,
        details,
        isPreview,
        productProperties,
        etimClassDetails,
        scrollContainer,
        i18n,
        language,
        initialCategories,
        onSelectTab = () => {},
        state,
    }) => {
    // Log delle props che arrivano
    /*console.log('article ==> ', article)
    console.log('details ==> ', details)
    console.log('productProperties ==> ', productProperties)
    console.log('etimClassDetails ==> ', etimClassDetails)
    console.log('scrollContainer ==> ', scrollContainer)
    console.log('initialCategories ==> ', initialCategories)*/


    const [activeCategories, setActiveCategories] = useState([...initialCategories]);
    const [showPrintWindow, setShowPrintWindow] = useState(false);
    const [showPopper, setShowPopper] = useState(false);

    // Get dell' url dell'immagine
    const { url: itemImageUrl = null, publicUrl: itemImagePublicUrl = null } = getProductImageUrl(details) || {};

    // Aggiunto una variabile locale per gestire l' url dell' item costruito da getAssetUrl
    const assetItemImageUrl = getAssetURL(encodeURIComponent(itemImageUrl))

    // Get dell' url dell' image placeholder
    const imagePlaceholder = state ? selectors.getDefaultPlaceholderImage(state)?.imageUrl : null;
    const imagePlaceholderUrl = imagePlaceholder === './images/no-photo.png' ? noPhotoImg : (imagePlaceholder || noPhotoImg);

    // Get dell' anteprima dell' image tramite l' hook per il file singolo (da uniformare in unico hook)
    const shouldUsePreview = !itemImageUrl || !itemImageUrl.startsWith('http');
    const { previewImage, loading: imageLoading, error: imageError } = useAssetPreview(
        shouldUsePreview ? itemImagePublicUrl : null
    );

    // Gestisco lo stato dell'immagine in modo più pulito
    const [currentImage, setCurrentImage] = useState(null);

    useEffect(() => {
        // Se abbiamo un URL HTTP diretto setCurrentImage url
        if (itemImageUrl && itemImageUrl.startsWith('http')) {
            setCurrentImage(itemImageUrl);
            return;
        }

        if(assetItemImageUrl && assetItemImageUrl.startsWith('http')) {
            setCurrentImage(assetItemImageUrl);
            return;
        }

        // Se stiamo ancora caricando l'anteprima, non aggiorno
        if (shouldUsePreview && imageLoading) {
            return;
        }

        // Se abbiamo l'anteprima, la imposto
        if (previewImage) {
            setCurrentImage(previewImage);
            return;
        }

        // Fallback all'URL pubblico
        if (itemImagePublicUrl) {
            setCurrentImage(itemImagePublicUrl);
            return;
        }

        // Nessuna immagine disponibile
        setCurrentImage(null);
    }, [itemImageUrl, previewImage, itemImagePublicUrl, imageLoading, shouldUsePreview]);


    useEffect(() => {
        const prevActiveCategoriesLength = activeCategories.length;
        if (prevActiveCategoriesLength > 0) {
            onSelectTab(last(activeCategories));
        }
    }, [activeCategories, onSelectTab]);

    const handleTabClick = (key) => {
        setActiveCategories((prevState) => xor(prevState, [key]));
    };

    const getActiveCategoriesOrdered = () => {
        return orderedCategoriesKeys.reduce((result, key) => {
            if (includes(activeCategories, key)) {
                result.push(key);
            }
            return result;
        }, []);
    };

    const toggleAllCategories = () => {
        const valid = getValidCategories(details);

        if (activeCategories.length !== valid.length) {
            setActiveCategories(valid);
        } else {
            setActiveCategories([]);
        }
    };

    const getCanExpandAll = () => {
        const valid = getValidCategories(details);
        return activeCategories.length !== valid.length;
    };

    const getCategory = (key) => {
        const category = find(categories, { key });
        return category;
    };

    const showPrintWindowHandler = () => {
        setShowPrintWindow(true);
    };

    const hidePrintWindow = () => {
        setShowPrintWindow(false);
    };

    const getPrintWindowTitle = () => {
        return `${getMarcaArticolo(details)} - ${getCodiceArticolo(details)} - ${getDescrizioneArticolo(details)}`;
    };

    const togglePopper = () => {
        setShowPopper(!showPopper);
    };

    const canExpandAll = getCanExpandAll();
    const shouldShowBottomMargin = window.innerWidth < 640;

    return (
        <Fragment>
            <Manager>
                {showPrintWindow && (
                    <ArticlePrintWindow
                        title={getPrintWindowTitle()}
                        onUnload={hidePrintWindow}
                        article={article}
                        details={details}
                        activeCategories={activeCategories}
                        etimClassDetails={etimClassDetails}
                        isPreview={true}
                    />
                )}
                <div
                    className="grid grid-cols-12"
                    style={{ marginBottom: shouldShowBottomMargin ? '80px' : '0' }}
                >
                    <div className="container-article-recap col-span-12">
                        <div className="grid grid-cols-12">
                            <div className="col-span-12 md:col-span-9">
                            <ArticleRecap
                                language={language}
                                item={article}
                                details={details}
                                productProperties={productProperties}
                            />
                        </div>
                            <div className="col-span-12 md:col-span-3" style={{ position: 'relative' }}>
                            <div
                                className="article-details-img-wrapper pb-4"

                            >
                                <div className="w-full">
                                    {imageLoading && (
                                        <div className="rounded bg-secondary table-img-preview" />
                                    )}
                                    {!imageLoading && currentImage && (
                                        <img
                                            src={currentImage}
                                            className="img-responsive m-a item-main-image"
                                            style={{ maxWidth: '100%' }}
                                            onError={(e) => {
                                                e.target.src = imagePlaceholderUrl;
                                            }}
                                            alt="Product"
                                        />
                                    )}
                                    {!imageLoading && !currentImage && (
                                        <img
                                            src={imagePlaceholderUrl}
                                            className="img-responsive m-a"
                                            style={{ maxWidth: '100%' }}
                                            alt="Placeholder"
                                        />
                                    )}
                                </div>
                            </div>
                            {isPreview === false && (
                                <div className="article-ftp-button-holder text-right">
                                    {getScontoAumento(details) && (
                                        <Reference>
                                            {({ ref }) => (
                                                <button
                                                    onClick={togglePopper}
                                                    ref={ref}
                                                    className="btn mr-1"
                                                >
                                                    <FaPercent />
                                                </button>
                                            )}
                                        </Reference>
                                    )}
                                    <button
                                        className={cx('btn tooltip', {
                                            'btn-primary': !canExpandAll,
                                        })}
                                        onClick={toggleAllCategories}
                                        data-tooltip={
                                            canExpandAll
                                                ? i18n._('sections:open:all')
                                                : i18n._('sections:close:all')
                                        }
                                    >
                                        {canExpandAll ? (
                                            <FaExpandArrowsAlt style={{ verticalAlign: '-10%' }} />
                                        ) : (
                                            <FaTimes style={{ verticalAlign: '-10%' }} />
                                        )}
                                    </button>{' '}
                                    <button
                                        className="btn btn-success"
                                        onClick={showPrintWindowHandler}
                                    >
                                        <i className="icon icon-download" />{' '}
                                        <span className="text-uppercase">
                                        <Trans id="Pdf Sheet" />
                                    </span>
                                    </button>
                                </div>
                            )}
                        </div>
                        </div>
                    </div>

                    <ArticleDetailsCategoryTab
                        onTabClick={handleTabClick}
                        activeCategories={activeCategories}
                        details={details}
                        scrollOnClick={true}
                        scrollContainer={scrollContainer}
                        productProperties={productProperties}
                        etimClassDetails={etimClassDetails}
                        item={article}
                        language={language}
                    />
                </div>
                <Popper
                    placement="top"
                    strategy="fixed"
                    modifiers={[
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 8],
                            },
                        },
                    ]}
                >
                    {(props) => {
                        return showPopper ? (
                            <div
                                ref={props.ref}
                                style={props.style}
                                data-placement={props.placement}
                            >
                                <ModalScontoAumento
                                    onClose={togglePopper}
                                    details={details}
                                />
                            </div>
                        ) : null;
                    }}
                </Popper>
            </Manager>
        </Fragment>
    );
};

const mapStateToProps = (state, ownProps) => {
    return {
        productProperties: selectors.getProductProperties(state),
        language: selectors.getLanguage(state),
        state: state,
    };
};

const ConnectedArticleDetailsBody = connect(mapStateToProps)(ArticleDetailsBody);

export default withI18n()(ConnectedArticleDetailsBody);